import { FC, FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Headline = styled(Text)`
  margin-bottom: 4px;
`;

const Options = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 12px;
`;

const Option = styled.button.attrs({ type: 'button' })<{ $active: boolean }>`
  flex: 1;
  padding: 8px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.purple};
    `}
`;

const OptionBadge = styled(Text).attrs({
  $type: 'extraSmall',
  as: 'span',
  $color: 'gray',
})`
  border-radius: 4px;
  margin-right: 8px;
  display: flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

interface Props {
  onClose: VoidFunction;
}

const NewCompilationModal: FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const [type, setType] = useState<'final' | 'test' | null>(null);
  const [compilationType, setCompilationType] = useState<
    'version' | 'compilation' | null
  >(null);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    navigate(`/general/video-compilations/create/${type}/${compilationType}`);
  };

  const submitDisabled = !type || !compilationType;

  return (
    <Modal zIndex={50}>
      <SidePanel heading="Create New" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <Grid>
              <div>
                <Headline $type="smallHeadline">Select Destination</Headline>
                <Text $color="gray" $type="smallNormal">
                  Determine whether this is for final delivery or testing
                  purposes
                </Text>
                <Options>
                  <Option
                    $active={type === 'final'}
                    onClick={() => setType('final')}
                  >
                    <OptionBadge>F</OptionBadge>
                    <Text as="span" $type="smallHeadline">
                      Final
                    </Text>
                  </Option>
                  <Option
                    $active={type === 'test'}
                    onClick={() => setType('test')}
                  >
                    <OptionBadge>T</OptionBadge>
                    <Text as="span" $type="smallHeadline">
                      Test
                    </Text>
                  </Option>
                </Options>
              </div>
              <div>
                <Headline $type="smallHeadline">Select Type</Headline>
                <Text $color="gray" $type="smallNormal">
                  Determine whether this is a version build or a full
                  compilation
                </Text>
                <Options>
                  <Option
                    $active={compilationType === 'version'}
                    onClick={() => setCompilationType('version')}
                  >
                    <OptionBadge>V</OptionBadge>
                    <Text as="span" $type="smallHeadline">
                      Version
                    </Text>
                  </Option>
                  <Option
                    $active={compilationType === 'compilation'}
                    onClick={() => setCompilationType('compilation')}
                  >
                    <OptionBadge>C</OptionBadge>
                    <Text as="span" $type="smallHeadline">
                      Compilation
                    </Text>
                  </Option>
                </Options>
              </div>
            </Grid>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button type="submit" disabled={submitDisabled}>
              Start
            </Button>
            <Button type="button" color="secondary" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default NewCompilationModal;
